// src/pages/AdminPanel.js
import React, { useState, useEffect } from 'react';
import { ContentLayout, PageContainer } from '../components/layout/Layout';
import { useBusinessConfig } from '../context/BusinessConfigContext';
import { adminAPI } from '../services/api/adminAPI';
import { Shield, CheckCircle, XCircle, User, Building, Phone, Mail, Calendar, Globe } from 'lucide-react';
import GrowRegistrationPanel from './GrowRegistrationPanel';
import MessageUsageChart from '../components/charts/MessageUsageChart';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useUserSession } from '../context/UserSessionContext';

export function AdminPanel({ onNavigate }) {
  const { isAdmin } = useBusinessConfig();
  const [accessRequests, setAccessRequests] = useState([]);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    isImpersonating,
    impersonatedUserId,
    startImpersonation,
    stopImpersonation,
  } = useUserSession();

  // Fetch users from business_configs collection
  useEffect(() => {
    fetchUsersFromBusinessConfigs();
  }, []);

  const fetchUsersFromBusinessConfigs = async () => {
    try {
      setLoading(true);
      const businessConfigsRef = collection(db, 'business_configs');
      const snapshot = await getDocs(businessConfigsRef);

      const usersList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        usersList.push({
          uid: doc.id, // The document ID is the user ID
          email: data.email || 'No email',
          businessName: data.business_name || data.businessName,
          phone: data.phone || data.business_phone,
          createdAt: data.createdAt,
          // Add any other relevant fields from business_configs
          ...data
        });
      });

      setUsers(usersList);

      // Auto-select first user if none selected
      if (usersList.length > 0 && !selectedUserId) {
        setSelectedUserId(usersList[0].uid);
      }
    } catch (error) {
      console.error('Error fetching users from business_configs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadAccessRequests();
    }
  }, [isAdmin]);

  const handleStartImpersonation = () => {
    if (selectedUserId) {
      startImpersonation(selectedUserId);
    }
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
  };

  const handleFetchMessageUsage = async (startMonth, endMonth) => {
    if (!selectedUserId) {
      throw new Error('No user selected');
    }

    try {
      const result = await adminAPI.getUserMessageUsage(selectedUserId, startMonth, endMonth);
      return result.data;
    } catch (error) {
      console.error('Error fetching message usage:', error);
      throw error;
    }
  };

  const loadAccessRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call real API instead of using mock data
      const result = await adminAPI.getAccessRequests();
      setAccessRequests(result.requests || []);
    } catch (err) {
      console.error('Error loading access requests:', err);
      setError(err.message);

      // Fallback to empty array on error
      setAccessRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setProcessing(prev => new Set(prev).add(requestId));

      await adminAPI.approveAccessRequest(requestId);

      // Refresh the list
      await loadAccessRequests();
    } catch (err) {
      console.error('Error approving request:', err);
      alert('Failed to approve request: ' + err.message);
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const handleReject = async (requestId) => {
    try {
      setProcessing(prev => new Set(prev).add(requestId));

      await adminAPI.rejectAccessRequest(requestId);

      // Refresh the list
      await loadAccessRequests();
    } catch (err) {
      console.error('Error rejecting request:', err);
      alert('Failed to reject request: ' + err.message);
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <ContentLayout
        currentPage="admin"
        onNavigate={onNavigate}
        title="ניהול מנהל"
        description="גישה למערכת ניהול המנהל"
      >
        <PageContainer>
          <div className="flex flex-col items-center justify-center py-12">
            <Shield className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">אין הרשאת מנהל</h3>
            <p className="text-gray-600 text-center max-w-md">
              אין לך הרשאה לגשת לעמוד זה. רק משתמשים עם הרשאות מנהל יכולים לראות בקשות גישה ולנהל משתמשים.
            </p>
          </div>
        </PageContainer>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      currentPage="admin"
      onNavigate={onNavigate}
      title="ניהול מנהל"
      description="ניהול בקשות גישה ומשתמשים במערכת"
    >
      <PageContainer>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
            <button
              onClick={loadAccessRequests}
              className="mt-3 text-red-600 hover:text-red-700 font-medium"
            >
              נסה שוב
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <User className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="mr-4">
                    <p className="text-sm font-medium text-gray-600">בקשות ממתינות</p>
                    <p className="text-2xl font-bold text-gray-900">{accessRequests.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="mr-4">
                    <p className="text-sm font-medium text-gray-600">אושרו השבוע</p>
                    <p className="text-2xl font-bold text-gray-900">-</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="mr-4">
                    <p className="text-sm font-medium text-gray-600">נדחו השבוע</p>
                    <p className="text-2xl font-bold text-gray-900">-</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Impersonation Controls */}
  {isAdmin && (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <h3 className="text-sm font-semibold text-yellow-800 mb-2">Admin Impersonation</h3>
      
      {isImpersonating ? (
        <div className="flex items-center justify-between">
          <span className="text-sm text-yellow-700">
            Currently impersonating: {users.find(u => u.uid === impersonatedUserId)?.businessName || 'Unknown'}
          </span>
          <button
            onClick={handleStopImpersonation}
            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Stop Impersonation
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm text-yellow-700">Select user to impersonate:</span>
          <button
            onClick={handleStartImpersonation}
            disabled={!selectedUserId}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Start Impersonation
          </button>
        </div>
      )}
    </div>
  )}

            {/* Access Requests Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">בקשות גישה</h3>
                <p className="text-sm text-gray-600 mt-1">
                  בקשות של משתמשים חדשים שממתינות לאישור
                </p>
              </div>

              {accessRequests.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">אין בקשות ממתינות</h3>
                  <p className="text-gray-600">כל הבקשות כבר טופלו או שלא נשלחו בקשות חדשות.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          פרטי משתמש
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          פרטי עסק
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          תאריך בקשה
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          פעולות
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {accessRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-semibold">
                                  {request.displayName?.[0] || request.email?.[0] || 'U'}
                                </span>
                              </div>
                              <div className="mr-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {request.displayName || 'משתמש ללא שם'}
                                </div>
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Mail className="w-3 h-3 ml-1" />
                                  {request.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-gray-900 flex items-center">
                                <Building className="w-3 h-3 ml-1" />
                                {request.businessInfo?.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {request.businessInfo?.type}
                              </div>
                              <div className="text-xs text-gray-400 flex items-center">
                                <Phone className="w-3 h-3 ml-1" />
                                {request.businessInfo?.phone}
                              </div>
                              {request.businessInfo?.website && (
                                <div className="text-xs text-gray-400 flex items-center">
                                  <Globe className="w-3 h-3 ml-1" />
                                  {request.businessInfo.website}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="w-3 h-3 ml-1" />
                              {new Date(request.requestDate).toLocaleDateString('he-IL')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleApprove(request.id)}
                                disabled={processing.has(request.id)}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {processing.has(request.id) ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent ml-1"></div>
                                ) : (
                                  <CheckCircle className="w-3 h-3 ml-1" />
                                )}
                                אשר
                              </button>
                              <button
                                onClick={() => handleReject(request.id)}
                                disabled={processing.has(request.id)}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {processing.has(request.id) ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent ml-1"></div>
                                ) : (
                                  <XCircle className="w-3 h-3 ml-1" />
                                )}
                                דחה
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="min-h-screen bg-gray-50">
          <div className="container mx-auto p-6">
            {/* Users List Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Business Users</h2>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading users...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {users.map((user) => (
                    <div
                      key={user.uid}
                      onClick={() => setSelectedUserId(user.uid)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedUserId === user.uid
                          ? 'border-purple-500 bg-purple-50 shadow-lg'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">
                            {user.businessName || 'Unnamed Business'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">{user.email}</p>
                          {user.phone && (
                            <p className="text-sm text-gray-500 mt-1">📞 {user.phone}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            ID: {user.uid.substring(0, 8)}...
                          </p>
                        </div>
                        {selectedUserId === user.uid && (
                          <div className="ml-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-600 rounded-full">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {users.length === 0 && !loading && (
                <div className="text-center py-8 bg-gray-100 rounded-xl">
                  <p className="text-gray-600">No users found in business_configs</p>
                </div>
              )}
            </div>

            {/* Selected User Info Bar */}
            {selectedUserId && (
              <div className="mb-6 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Currently selected:</p>
                    <p className="font-semibold text-gray-900">
                      {users.find(u => u.uid === selectedUserId)?.businessName || 'Unknown'}
                    </p>
                  </div>
                  <button
                    onClick={fetchUsersFromBusinessConfigs}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Refresh Users
                  </button>
                </div>
              </div>
            )}

            {/* GROW Registration Panel - Always Visible */}
            {selectedUserId ? (
              <GrowRegistrationPanel
                userId={selectedUserId}
                adminAPI={adminAPI}
              />
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <div className="max-w-sm mx-auto">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Select a User to Begin
                  </h3>
                  <p className="text-gray-600">
                    Choose a business user from the list above to manage their GROW payment registration
                  </p>
                </div>
              </div>
            )}

            {/* Message Usage Chart */}
            {selectedUserId && (
              <div className="mt-6">
                <MessageUsageChart onFetchData={handleFetchMessageUsage} />
              </div>
            )}
          </div>
        </div>
      </PageContainer>
    </ContentLayout>
  );
}